import type { ChatContext } from './chat-context-types';

export type StarterPromptContext =
    | 'alignment-employee'
    | 'alignment-manager'
    | 'alignment-team'
    | 'any'
    | 'cluster'
    | 'family'
    | 'level'
    | 'personal-home'
    | 'role';

export type StarterPromptKey =
    | 'alignment-employee-comments'
    | 'alignment-employee-automations'
    | 'alignment-employee-next'
    | 'alignment-manager-comments'
    | 'alignment-manager-decide'
    | 'alignment-manager-summary'
    | 'alignment-team-start'
    | 'alignment-team-waiting'
    | 'any-next-steps'
    | 'any-no-reviewer'
    | 'any-strengths'
    | 'cluster-duplicate-skills'
    | 'cluster-excel-overview'
    | 'family-duplicate-skills'
    | 'family-excel-overview'
    | 'level-compare-merge'
    | 'level-review'
    | 'role-compare-merge'
    | 'role-review';

type StarterPrompt = {
    readonly key: StarterPromptKey;
    /** Prompts that lead to changing content are hidden for users who cannot edit. */
    readonly requiresEdit: boolean;
};

const MAX_STARTER_PROMPTS = 4;

const prompt = (key: StarterPromptKey, requiresEdit = false): StarterPrompt => ({ key, requiresEdit });

const ANY_PROMPTS: readonly StarterPrompt[] = [
    prompt('any-strengths'),
    prompt('any-no-reviewer'),
    prompt('any-next-steps'),
];

const CONTEXT_PROMPTS: Record<StarterPromptContext, readonly StarterPrompt[]> = {
    'alignment-employee': [
        prompt('alignment-employee-next'),
        prompt('alignment-employee-comments'),
        prompt('alignment-employee-automations'),
    ],
    'alignment-manager': [
        prompt('alignment-manager-summary'),
        prompt('alignment-manager-comments'),
        prompt('alignment-manager-decide'),
    ],
    'alignment-team': [prompt('alignment-team-waiting'), prompt('alignment-team-start')],
    any: [],
    cluster: [prompt('cluster-duplicate-skills'), prompt('cluster-excel-overview')],
    family: [prompt('family-duplicate-skills'), prompt('family-excel-overview')],
    level: [prompt('level-review', true), prompt('level-compare-merge')],
    'personal-home': [],
    role: [prompt('role-review', true), prompt('role-compare-merge')],
};

export const resolveStarterPromptContext = (context: ChatContext | null): StarterPromptContext => {
    if (context === null) return 'any';
    switch (context.kind) {
        case 'job-family':
            return 'family';
        case 'job-cluster':
            return 'cluster';
        case 'job-profile':
            return context.itemType === 'job-level' ? 'level' : 'role';
        case 'position-alignment':
            return context.viewer === 'EMPLOYEE' ? 'alignment-employee' : 'alignment-manager';
        case 'position-alignment-team':
            return 'alignment-team';
        case 'personal-home':
            return 'personal-home';
        case 'jsa':
            if (context.selectedLevelId !== undefined) return 'level';
            if (context.selectedRoleId !== undefined) return 'role';
            if (context.selectedClusterId !== undefined) return 'cluster';
            if (context.selectedFamilyId !== undefined) return 'family';
            return 'any';
        default:
            return 'any';
    }
};

// The general prompts are about the job architecture, so the alignment pages and home only offer their own.
const OWN_PROMPTS_ONLY_CONTEXTS: ReadonlySet<StarterPromptContext> = new Set([
    'alignment-employee',
    'alignment-manager',
    'alignment-team',
    'personal-home',
]);

export const selectStarterPrompts = (context: StarterPromptContext, canEdit: boolean): readonly StarterPromptKey[] =>
    [...CONTEXT_PROMPTS[context], ...(OWN_PROMPTS_ONLY_CONTEXTS.has(context) ? [] : ANY_PROMPTS)]
        .filter((candidate) => canEdit || !candidate.requiresEdit)
        .slice(0, MAX_STARTER_PROMPTS)
        .map((candidate) => candidate.key);

export const shouldShowStarterPrompts = (state: {
    readonly hasPendingSend: boolean;
    readonly isError: boolean;
    readonly isLoading: boolean;
    readonly isStreaming: boolean;
    readonly messageCount: number;
}): boolean =>
    state.messageCount === 0 && !state.isLoading && !state.isError && !state.isStreaming && !state.hasPendingSend;
